import {Component, Input, OnDestroy, OnInit} from '@angular/core';
import {
    AangifteSchenkbelasting,
    Adres,
    ContactPersoon,
    GiftTaxEntity, IntermediaryEntity,
} from "@snelstart-ventures/fiscaal/domain-logic";
import {FormBuilder, FormGroup, ReactiveFormsModule} from "@angular/forms";
import {BokkzAlgDataService, BokkzSbDataService} from "@snelstart-ventures/fiscaal/domain-logic";
import {SharedUiDetailsModule} from "@snelstart/ui-details";
import {SharedUiFormControlsModule} from "@snelstart/ui-form-controls";
import {Subscription} from "rxjs";
import {SharedUiAlertModule} from "@snelstart/ui-alert";
import {SharedUiTooltipTriggerModule} from "@snelstart/ui-tooltip-trigger";

@Component({
    selector: 'snelstart-venture-fiscaal-feature-contactpersoon',
    standalone: true,
    imports: [
        ReactiveFormsModule,
        SharedUiDetailsModule,
        SharedUiFormControlsModule,
        SharedUiAlertModule,
        SharedUiTooltipTriggerModule
    ],
    templateUrl: './contactpersoon.component.html',
    styleUrl: './contactpersoon.component.css'
})
export class ContactpersoonComponent implements OnInit, OnDestroy {

    @Input() public id!: string;
    private intermediaryEntitySubscription: Subscription | undefined;
    private giftTaxEntitySubscription: Subscription | undefined;
    private defaultFieldSubscriptions: Subscription | undefined;
    public intermediary = {} as IntermediaryEntity;
    public giftTaxEntity = {} as GiftTaxEntity;
    public sbAangifte = {} as AangifteSchenkbelasting;
    public persoonFg = {} as FormGroup;
    public beconNummer: string | undefined;
    public tooltips = {
        telefoonnummer: {
            icon: 'circle-info',
            tooltip: 'Het telefoonnummer dient 10 cijfers te bevatten zonder tussenstreepje',
            placement: 'right'
        }
    } as const;
    private readonly userEditedDefaultFields: Record<'beconnummer' | 'emailadres' | 'instellingnaam', boolean> = {
        beconnummer: false,
        emailadres: false,
        instellingnaam: false,
    };

    constructor(
        public algDataService: BokkzAlgDataService,
        public dataService: BokkzSbDataService,
        private fb: FormBuilder) {
        this.persoonFg = this.fb.group<ContactPersoon>({
            ontvanger: null as unknown as string,
            protocolNummerNotaris: null as unknown as string,
            beconnummer: null as unknown as string,
            emailadres: null as unknown as string,
            instellingnaam: null as unknown as string,
            telefoonnummer: null as unknown as string,
            roepnaam: null as unknown as string,
            voorletters: null as unknown as string,
            tussenvoegsels: null as unknown as string,
            achternaam: null as unknown as string,
            bsn: null as unknown as string,
            geboortedatum: null as unknown as Date,
            overlijdensdatum: null as unknown as Date,
            landCodeNationaliteit: null as unknown as string,
            adres: null as unknown as Adres,
        })
    }

    ngOnInit(): void {
        this.trackDefaultFieldEdits();

        this.intermediaryEntitySubscription = this.algDataService.intermediary.subscribe((value) => {
            this.intermediary = value;
            const formValue = this.persoonFg.getRawValue();
            this.persoonFg.patchValue({
                beconnummer: this.resolveDefaultValue('beconnummer', formValue.beconnummer, undefined, value.beconId),
                emailadres: this.resolveDefaultValue('emailadres', formValue.emailadres, undefined, value.officeEmail),
                instellingnaam: this.resolveDefaultValue('instellingnaam', formValue.instellingnaam, undefined, value.officeName),
            }, {emitEvent: false});
        })
        this.giftTaxEntitySubscription = this.dataService.giftTaxEntity.subscribe((value) => {
            this.giftTaxEntity = value;
            this.sbAangifte = value.aangifte!;
            const contactPersoon = value.aangifte?.contactPersoon;
            const formValue = this.persoonFg.getRawValue();
            this.persoonFg.patchValue({
                ...contactPersoon,
                beconnummer: this.resolveDefaultValue('beconnummer', formValue.beconnummer, contactPersoon?.beconnummer, this.intermediary.beconId),
                emailadres: this.resolveDefaultValue('emailadres', formValue.emailadres, contactPersoon?.emailadres, this.intermediary.officeEmail),
                instellingnaam: this.resolveDefaultValue('instellingnaam', formValue.instellingnaam, contactPersoon?.instellingnaam, this.intermediary.officeName),
            }, {emitEvent: false})
        })
    }

    ngOnDestroy(): void {
        if (this.intermediaryEntitySubscription) {
            this.intermediaryEntitySubscription.unsubscribe()
        }
        if (this.giftTaxEntitySubscription) {
            this.giftTaxEntitySubscription.unsubscribe()
        }
        if (this.defaultFieldSubscriptions) {
            this.defaultFieldSubscriptions.unsubscribe()
        }
    }

    saveForm(): void {
        this.giftTaxEntity.aangifte = {
            ...this.sbAangifte,
            contactPersoon: {
                ...this.sbAangifte.contactPersoon,
                protocolNummerNotaris: this.persoonFg.value.protocolNummerNotaris,
                beconnummer: this.persoonFg.value.beconnummer,
                emailadres: this.persoonFg.value.emailadres,
                instellingnaam: this.persoonFg.value.instellingnaam,
                telefoonnummer: this.persoonFg.value.telefoonnummer,
                roepnaam: this.persoonFg.value.roepnaam,
                voorletters: this.persoonFg.value.voorletters,
                tussenvoegsels: this.persoonFg.value.tussenvoegsels,
                achternaam: this.persoonFg.value.achternaam,
                bsn: this.persoonFg.value.bsn,
                geboortedatum: this.persoonFg.value.geboortedatum,
                overlijdensdatum: this.persoonFg.value.overlijdensdatum,
                landCodeNationaliteit: this.persoonFg.value.landCodeNationaliteit,
            },
        } as AangifteSchenkbelasting;
        this.dataService.updateGiftTaxEntity(this.giftTaxEntity);
    }

    private trackDefaultFieldEdits(): void {
        this.defaultFieldSubscriptions = new Subscription();

        this.defaultFieldSubscriptions.add(this.persoonFg.controls['beconnummer']?.valueChanges.subscribe(() => {
            this.userEditedDefaultFields.beconnummer = true;
        }));
        this.defaultFieldSubscriptions.add(this.persoonFg.controls['emailadres']?.valueChanges.subscribe(() => {
            this.userEditedDefaultFields.emailadres = true;
        }));
        this.defaultFieldSubscriptions.add(this.persoonFg.controls['instellingnaam']?.valueChanges.subscribe(() => {
            this.userEditedDefaultFields.instellingnaam = true;
        }));
    }

    private resolveDefaultValue(
        field: 'beconnummer' | 'emailadres' | 'instellingnaam',
        currentValue: string | null | undefined,
        preferredValue: string | null | undefined,
        fallbackValue: string | null | undefined,
    ): string | null | undefined {
        if (this.userEditedDefaultFields[field]) {
            return currentValue;
        }

        return this.firstDefined(currentValue, preferredValue, fallbackValue);
    }

    private firstDefined(...values: Array<string | null | undefined>): string | null | undefined {
        return values.find((value) => typeof value === 'string' ? value.trim().length > 0 : value != null);
    }

}
